'use client'

import { useLatest } from '@/core/hooks/use-latest'
import { useRouter } from 'next/navigation'
import { useContext, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useSWRConfig } from 'swr'
import type { ZodType } from 'zod'

import { buildSwrCacheKey } from '@/core/lib/swr/swr.key'
import { swrFetcher } from '@/core/lib/swr/swr.client'
import RenewGate from '@/features/studio-renew/components/renew-gate'
import { StudioCollectionResource } from '@/features/studio-renew/components/studio-collection-resource'
import { StudioValidationBoundary } from '@/features/studio-renew/components/validation-session'
import {
  emptyMaterial,
  isMaterialDraftId,
} from '@/features/studio-renew/lib/material-draft'
import {
  normalizeStudioCollection,
  STUDIO_READ_POLICY,
  studioSwrKey,
} from '@/features/studio-renew/lib/studio-read'
import {
  emptyWork,
  WORK_DRAFT_ID,
} from '@/features/studio-renew/lib/work-draft'
import { studioCollectionSchemas } from '@/features/studio-renew/schemas/studio-read.schema'
import {
  RenewStoreSnapshotContext,
  useRenewStore,
} from '@/features/studio-renew/store'
import {
  acceptStudioCollections,
  beginMaterialDraft,
  beginStudioCollectionRead,
  beginWorkDraft,
  connectStudioReader,
  recordStudioCollectionError,
  useStudioCollectionStatus,
} from '@/features/studio-renew/sync'
import { useSessionStore } from '@/features/veluvit/stores/session.store'
import { AppRefreshContext } from '@/features/veluvit/hooks/use-app-read-scope'

import type { StudioCollectionResponse } from '@/features/studio-renew/schemas/studio-read.schema'
import type { StudioInitialSnapshot } from '@/features/studio-renew/services/studio-read.server'
import type { Kind } from '@/features/studio-renew/store'
import type { ReactNode } from 'react'

type Props = {
  initial: StudioInitialSnapshot
  draft?: { kind: Kind; id: string }
  children: ReactNode
}
const COLLECTION = {
  chat: 'chats',
  character: 'characters',
  world: 'worlds',
  status: 'statuses',
  command: 'commands',
} as const

export default function StudioDataProvider({
  initial,
  draft,
  children,
}: Props) {
  const { mutate } = useSWRConfig()
  const router = useRouter()
  const requestRefresh = useContext(AppRefreshContext)
  const sessionReady = useSessionStore((state) => state.ready)
  const sessionOwner = useSessionStore((state) => state.data?.user?.id)
  const sessionGuest = useSessionStore((state) => state.data?.user?.guest)
  const identity = useSessionStore((state) => state.identity)
  const [initialIdentity] = useState(() => identity)
  const refreshed = useRef<string | null>(null)
  const initialized = useRef<string | null>(null)
  const [resources, setResources] = useState(
    () => Object.keys(initial.collections) as Kind[]
  )
  const resourceKinds = useLatest(resources)
  const owner =
    initial.auth.state === 'member'
      ? initial.auth.owner
      : initial.auth.state === 'error' && sessionReady && !sessionGuest
        ? (sessionOwner ?? null)
        : null
  const active =
    !!owner &&
    (sessionReady
      ? sessionOwner === owner && !sessionGuest
      : identity === initialIdentity)
  const snapshot = useMemo(() => {
    const state = { ...useRenewStore.getInitialState(), owner }
    for (const kind of Object.keys(initial.collections) as Kind[]) {
      const response = initial.collections[kind]
      if (response?.ok)
        Object.assign(state, {
          [COLLECTION[kind]]: normalizeStudioCollection(kind, response.data),
        })
    }
    if (draft && !state[COLLECTION[draft.kind]][draft.id]) {
      const item =
        draft.kind === 'chat' ? emptyWork() : emptyMaterial(draft.kind)
      Object.assign(state, {
        [COLLECTION[draft.kind]]: {
          ...state[COLLECTION[draft.kind]],
          [draft.id]: { ...item, id: draft.id, updatedAt: 0 },
        },
      })
    }
    return {
      state,
      readyKinds: Object.keys(initial.collections) as Kind[],
      scope: initial.scope,
    }
  }, [initial, owner, draft])

  useLayoutEffect(() => {
    if (!active || !owner) return
    if (
      initialized.current !== initial.scope ||
      useRenewStore.getState().owner !== owner
    ) {
      useRenewStore.getState().setOwner(owner)
      const values: Partial<Record<Kind, StudioCollectionResponse>> = {}
      for (const kind of Object.keys(initial.collections) as Kind[]) {
        const response = initial.collections[kind]
        if (response?.ok) values[kind] = response.data
      }
      acceptStudioCollections(owner, values)
      if (
        draft?.kind === 'chat' &&
        draft.id === WORK_DRAFT_ID &&
        !useRenewStore.getState().chats[draft.id]
      )
        beginWorkDraft()
      if (
        draft &&
        draft.kind !== 'chat' &&
        isMaterialDraftId(draft.id) &&
        !useRenewStore.getState()[COLLECTION[draft.kind]][draft.id]
      )
        beginMaterialDraft(draft.kind, {}, draft.id)
      initialized.current = initial.scope
    }
    const flights = new Map<Kind, Promise<void>>()
    const read = (kind: Kind) => {
      const existing = flights.get(kind)
      if (existing) return existing
      setResources((current) =>
        current.includes(kind) ? current : [...current, kind]
      )
      const session = useSessionStore.getState()
      const token = beginStudioCollectionRead(owner, kind)
      const current = () => {
        const latest = useSessionStore.getState()
        return (
          token.current() &&
          useRenewStore.getState().owner === owner &&
          latest.identity === session.identity &&
          (!latest.ready ||
            (latest.data?.user?.id === owner && !latest.data.user.guest))
        )
      }
      useStudioCollectionStatus.setState((state) => ({
        byKind: {
          ...state.byKind,
          [kind]: state.byKind[kind] === 'ready' ? 'ready' : 'loading',
        },
      }))
      const key = studioSwrKey(initial.scope, kind)
      const request = mutate(
        buildSwrCacheKey(key, 'REALTIME'),
        async () => {
          const response = await swrFetcher(
            key,
            'REALTIME',
            STUDIO_READ_POLICY,
            studioCollectionSchemas[kind] as ZodType<StudioCollectionResponse>
          )
          if (response.ok && current())
            acceptStudioCollections(
              owner,
              { [kind]: response.data },
              token.versions
            )
          return response
        },
        { revalidate: false }
      )
        .then(() => {})
        .catch((error: unknown) => {
          if (current()) recordStudioCollectionError(kind, error)
          throw error
        })
        .finally(() => {
          flights.delete(kind)
        })
      flights.set(kind, request)
      return request
    }
    const disconnect = connectStudioReader(owner, read)
    for (const kind of resourceKinds.current) {
      if (useStudioCollectionStatus.getState().byKind[kind] !== 'ready')
        void read(kind).catch(() => {})
    }
    return disconnect
  }, [active, owner, initial, draft, identity, mutate, resourceKinds])

  useLayoutEffect(() => {
    if (!sessionReady || active) return
    const account = `${sessionOwner ?? 'guest'}:${sessionGuest}`
    if (refreshed.current === account) return
    refreshed.current = account
    if (requestRefresh) requestRefresh()
    else router.refresh()
  }, [active, router, requestRefresh, sessionReady, sessionOwner, sessionGuest])

  return (
    <RenewStoreSnapshotContext.Provider value={active ? snapshot : null}>
      <RenewGate initial={initial.auth}>
        {active &&
          resources.map((kind) => (
            <StudioCollectionResource
              key={kind}
              owner={owner!}
              kind={kind}
              epoch={initial.scope}
            />
          ))}
        <StudioValidationBoundary>{children}</StudioValidationBoundary>
      </RenewGate>
    </RenewStoreSnapshotContext.Provider>
  )
}
